/**
 * Page « Mes offres » du dashboard client (/dashboard/offres).
 *
 * Affiche les packs actifs en provenance de l'API publique /packs avec
 * description et fourchette de prix HT.
 *
 * Comportement au clic « Choisir ce pack » :
 *  - Aucun projet         → redirige vers /projects/new?pack=<code>.
 *  - Un projet sans pack  → dialog de confirmation puis PATCH du projet.
 *  - Plusieurs sans pack  → dialog avec sélecteur de projet.
 *  - Tous packs assignés  → message + bouton "Créer un nouveau projet".
 */
import { packCodeSchema, type Pack, type PackCode, type Project } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Package } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import { listActivePacks } from '@/features/projects/packs-public-api';
import {
  listProjects,
  updateProject,
} from '@/features/projects/projects-api';

function formatPrice(min: number, max: number): string {
  if (min === max) return `${min.toFixed(0)} € HT`;
  return `${min.toFixed(0)} – ${max.toFixed(0)} € HT`;
}

type Mode =
  | { kind: 'confirm'; projectId: string; pack: Pack }
  | { kind: 'choose'; pack: Pack }
  | { kind: 'all-taken'; pack: Pack };

export function DashboardOffresPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<Mode | null>(null);
  const [selectedProject, setSelectedProject] = useState('');

  const packs = useQuery({ queryKey: ['packs', 'active'], queryFn: listActivePacks });
  const projects = useQuery({
    queryKey: ['projects', 'mine'],
    queryFn: listProjects,
  });

  // Auto-ouverture du flow si on arrive avec ?pack=<code> (depuis /offres
  // public ou /signup avec intent pack). On ne déclenche le flow qu'une fois
  // les deux queries chargées ; mode est null à ce stade (premier render).
  useEffect(() => {
    const raw = searchParams.get('pack');
    if (!raw) return;
    const parsed = packCodeSchema.safeParse(raw);
    if (!parsed.success) return;
    const list = packs.data ?? [];
    const found = list.find((p) => p.code === parsed.data);
    if (!found || !projects.data) return;
    handlePackClick(found);
    searchParams.delete('pack');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packs.data, projects.data]);

  const attachMut = useMutation({
    mutationFn: (params: { projectId: string; packCode: PackCode }) =>
      updateProject(params.projectId, { packCode: params.packCode }),
    onSuccess: (p) => {
      toast.success('Pack associé au dossier');
      setMode(null);
      qc.invalidateQueries({ queryKey: ['projects', 'mine'] });
      navigate(`/projects/${p.id}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function handlePackClick(pack: Pack) {
    // Appelé uniquement depuis un onClick du rendu, qui n'existe qu'après
    // chargement des deux queries — projects.data est donc défini.
    const list: Project[] = projects.data!;
    const noPack = list.filter((p) => !p.packCode);

    if (list.length === 0) {
      navigate(`/projects/new?pack=${pack.code}`);
      return;
    }
    if (noPack.length === 0) {
      setMode({ kind: 'all-taken', pack });
      return;
    }
    if (noPack.length === 1) {
      setSelectedProject(noPack[0]!.id);
      setMode({ kind: 'confirm', projectId: noPack[0]!.id, pack });
      return;
    }
    setSelectedProject('');
    setMode({ kind: 'choose', pack });
  }

  function handleConfirm(currentMode: Mode) {
    if (currentMode.kind === 'all-taken') {
      setMode(null);
      navigate(`/projects/new?pack=${currentMode.pack.code}`);
      return;
    }
    const targetId =
      currentMode.kind === 'confirm'
        ? currentMode.projectId
        : selectedProject;
    attachMut.mutate({ projectId: targetId, packCode: currentMode.pack.code });
  }

  const packList = packs.data ?? [];
  const projectList = projects.data ?? [];
  const noPackProjects = projectList.filter((p) => !p.packCode);

  return (
    <DashboardLayout title="Mes offres">
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold">Nos packs fibre</h2>
          <p className="text-sm text-gray-500">
            Choisissez le pack adapté et nous l’associons à votre dossier — ou{' '}
            <Link to="/projects/new" className="text-bcl-blue underline">
              démarrez un nouveau projet
            </Link>
            .
          </p>
        </div>

        {packs.isLoading ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : packList.length === 0 ? (
          <div className="rounded-md border bg-white p-6 text-center text-sm text-gray-500">
            Aucun pack disponible pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {packList.map((pack) => (
              <Card
                key={pack.id}
                className="border-2 border-transparent transition-colors hover:border-gray-200"
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-bcl-blue" />
                        {pack.name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {pack.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">
                    {formatPrice(pack.priceMinHt, pack.priceMaxHt)}
                  </p>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full"
                    onClick={() => handlePackClick(pack)}
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Choisir ce pack
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={mode !== null}
        onOpenChange={(open) => {
          if (!open) setMode(null);
        }}
      >
        <DialogContent>
          {mode?.kind === 'confirm' && (
            <>
              <DialogHeader>
                <DialogTitle>Associer le pack à votre dossier</DialogTitle>
                <DialogDescription>
                  Voulez-vous associer le pack <strong>{mode.pack.name}</strong>{' '}
                  au dossier{' '}
                  <strong>
                    {projectList.find((p) => p.id === mode.projectId)?.reference}
                  </strong>{' '}
                  ?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMode(null)}>
                  Annuler
                </Button>
                <Button
                  onClick={() => handleConfirm(mode)}
                  disabled={attachMut.isPending}
                >
                  {attachMut.isPending ? 'Association…' : 'Confirmer'}
                </Button>
              </DialogFooter>
            </>
          )}

          {mode?.kind === 'choose' && (
            <>
              <DialogHeader>
                <DialogTitle>Choisir le dossier</DialogTitle>
                <DialogDescription>
                  Vous avez plusieurs dossiers sans pack. Sélectionnez celui à
                  associer à <strong>{mode.pack.name}</strong>.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="offre-project">Dossier</Label>
                  <select
                    id="offre-project"
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">Sélectionner un dossier…</option>
                    {noPackProjects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.reference}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMode(null)}>
                  Annuler
                </Button>
                <Button
                  onClick={() => handleConfirm(mode)}
                  disabled={selectedProject === '' || attachMut.isPending}
                >
                  {attachMut.isPending ? 'Association…' : 'Confirmer'}
                </Button>
              </DialogFooter>
            </>
          )}

          {mode?.kind === 'all-taken' && (
            <>
              <DialogHeader>
                <DialogTitle>Tous vos dossiers ont déjà un pack</DialogTitle>
                <DialogDescription>
                  Pour souscrire à <strong>{mode.pack.name}</strong>, créez un
                  nouveau dossier — il sera pré-associé à ce pack.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMode(null)}>
                  Annuler
                </Button>
                <Button onClick={() => handleConfirm(mode)}>
                  Créer un nouveau projet
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
